import { forwardRef } from "react"

import { Select } from "@chakra-ui/react"
import { Option } from "types"

import {
  FormControl,
  SharedFormControlProps,
} from "components/common/FormControl"

import { COUNTRIES, STATES } from "data/dataSets"
import { optionize } from "data/utils"

interface SearchSelectProps extends SharedFormControlProps {
  name: string
  onChange: (value: any) => void
  value?: any
  type?: string
}

/**
 * A native select component to display a styled list of options.
 * - Use for any list of options that may be filled via autocomplete. e.g. countries, states, etc.
 */
export const SearchSelect = forwardRef<HTMLSelectElement, SearchSelectProps>(
  (props, ref) => {
    const {
      name,
      label,
      errorText,
      helperText,
      isDisabled,
      isInvalid,
      isReadOnly,
      isRequired,
      value,
      onChange,
      type,
    } = props

    let searchOptions: Option[] = []
    let placeholder = "Select an option"

    switch (type) {
      case "countrySearch":
        searchOptions = optionize(COUNTRIES)
        placeholder = "Select Country"
        break
      case "stateSearch":
        searchOptions = optionize(STATES)
        placeholder = "Select State"
        break
    }

    const selectValue =
      typeof value === "object" && value !== null && "value" in value
        ? value.value
        : value

    const FormControlValues = {
      errorText,
      helperText,
      isDisabled,
      isInvalid,
      isReadOnly,
      isRequired,
      label,
    }

    return (
      <FormControl {...FormControlValues}>
        <Select
          ref={ref}
          name={name}
          value={selectValue || ""}
          isDisabled={isDisabled || isReadOnly}
          onChange={e => {
            const selectedOption = searchOptions.find(
              o => o.value === e.target.value,
            )
            onChange(selectedOption)
          }}
          placeholder={placeholder}
        >
          {searchOptions.map(({ value, label }: Option) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </FormControl>
    )
  },
)
